import React, { useState, useEffect } from 'react';
import { useApi } from '../Api';
import { useAuth0 } from '@auth0/auth0-react';
import { Family } from '../types/Messaging';
import BalanceSheet from '../components/accounting/BalanceSheet';
import CashFlow from '../components/accounting/CashFlow';
import IncomeStatement from '../components/accounting/IncomeStatement';
import { SoftLedgerLocation, Entity } from '../types/Accounting';
import { PageMasthead } from '../components/PageMasthead';
import { FormSelect } from '../controls/FormSelect';

const Accounting: React.FC = () => {
  const api = useApi();
  const { getAccessTokenSilently } = useAuth0();
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | "">("");
  const [locations, setLocations] = useState<Entity[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'balance-sheet' | 'cash-flow' | 'income-statement'>('balance-sheet');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const familiesResponse = await api.get('/api/clientfamily/getfamilies');
        setFamilies(familiesResponse.data.families || []);
      } catch (err) {
        console.error('Error fetching families:', err);
        // Fallback to empty list if API fails
        setFamilies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: fetch once on mount; dep list left as-is to preserve existing behavior
  }, [getAccessTokenSilently]);

  // Fetch real locations from SoftLedger

  const fetchLocations = async (familyId: number) => {
    try {
      setLoading(true);

      const response = await api.get(`/api/accounting/Locations/me?familyId=${familyId}`);
      const locationTree: SoftLedgerLocation = response.data;

      // Convert location tree to flat entity list
      const locationsList: Entity[] = [];

      const processLocation = (location: SoftLedgerLocation, depth = 0) => {
        // Add all locations to the dropdown
        // This allows users to select parent locations for consolidated reports
        // and child locations for unconsolidated reports
        locationsList.push({
          id: location._id.toString(),
          name: location.name,
          locationId: location._id
        });

        // Process children recursively
        if (location.children) {
          location.children.forEach(child => processLocation(child, depth + 1));
        }
      };

      processLocation(locationTree);

      setLocations(locationsList);

      // Set the first entity as selected by default
      if (locationsList.length > 0) {
        setSelectedLocationId(locationsList[0].id);
      }

    } catch (error) {
      console.error('Error fetching locations:', error);
      // Fallback to empty list if API fails
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'balance-sheet', label: 'Balance Sheet', component: BalanceSheet },
    { id: 'cash-flow', label: 'Cash Flow', component: CashFlow },
    { id: 'income-statement', label: 'Income Statement', component: IncomeStatement },
  ] as const;

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-center">Loading accounting data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto dag-statement">
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="px-6 py-8">
            <div className="flex flex-col gap-6">
              {/* Title Section */}
              <PageMasthead eyebrow="Financials" title="Accounting Reports" subtitle="View financial statements and reports for your entities" />

              {/* Family Selector - Integrated below title */}
              <FormSelect
                btnTestId="family-dropdown"
                label="Family"
                placeholder="Pick a family"
                value={selectedFamilyId === "" ? null : selectedFamilyId}
                options={families.map(fam => ({ value: fam.id, label: fam.name }))}
                onChange={(val) => {
                  if (val === null) return;
                  setSelectedFamilyId(val as number);
                  fetchLocations(val as number);
                }}
              />

              {/* Location Selector - Integrated below title */}
              <FormSelect
                btnTestId="location-dropdown"
                label="Location"
                placeholder="Pick a location"
                value={selectedLocationId === "" ? null : selectedLocationId}
                options={locations.map(location => ({ value: location.id, label: location.name }))}
                onChange={(val) => setSelectedLocationId(val === null ? "" : String(val))}
              />
            </div>
          </div>
        </div>

        {/* Tabs Card */}
        <div className="bg-white rounded-xl shadow-md">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {ActiveComponent && (
              <ActiveComponent familyId={selectedFamilyId} locationId={selectedLocationId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accounting;
